import { ModelBase, ObservableCollection } from '@skylab-shared/core';
import { AddressModel, CalculationMethod, DocumentPaymentListItemModel, ExchangeRateModel, SaleAdvanceDocumentItemModel, SaleAdvanceDocumentModel, SaleDocumentAdjustmentModel, SaleDocumentItemModel, SaleDocumentKind, SaleDocumentModel, SaleGetDocumentDTO, SalePatternKsefSendOption, SalePatternModel, SaleProFormaModel, VatTableItemModel } from '@skylab-shared/dataModel';
import { RecursiveDiff } from '@skylab-shared/types';
import { Calc, MidpointRounding, dateDiffInDays, pickBy } from '@skylab-shared/utils';
import { ClientRepositoryBase } from '../../client/client.repo-base';
import { getCorrectNumberingIdFromSourceDocument } from '../../common/document-utils';
import { deserializeDocumentBuyerModel, deserializeDocumentClientModel } from '../../common/serialziation-utils';
import { SaleCommunityTransactionItem, SaleCommunityTransactionSymbol } from '../../community-transaction/queries/community-transaction-for-sale.query-base';
import { CompanyParameters } from '../../company/queries/company-parameters.query-base';
import { rewriteAddress, rewriteDocumentContractorWithFreshDataAsync } from '../../documents/utils/document-client-utils';
import { rewriteDocumentItem } from '../../documents/utils/document-item-utils';
import { NumberingItem } from '../../numbering/queries/numbering.query-base';
import { UserCollectionItem } from '../../user/queries/user.query-base';
import { VatRateItem } from '../../vat/queries/vat-rate-item';
import { VatReleaseDocumentModelPart } from '../parts/sale-document-vat-release.bo-part';
import { isAdjustment } from './sale-utils';

//#region Calculator provider

export function rewriteSaleDocumentItem(target: RecursiveDiff<SaleDocumentItemModel, ModelBase>, source: RecursiveDiff<SaleDocumentItemModel, ModelBase>): void {
    rewriteDocumentItem(target, source);
    target.cost = source.cost;
    target.unitCost = source.unitCost;
    target.jpkVatProcedure = source.jpkVatProcedure;
    target.includesInventoryControl = source.includesInventoryControl;
}

export function rewritePayment(target: RecursiveDiff<DocumentPaymentListItemModel, ModelBase>, source: RecursiveDiff<DocumentPaymentListItemModel, ModelBase>): void {
    target.id = source.id;
    target.date = source.date;
    target.value = source.value;
    target.onDocument = source.onDocument;
}

export function rewriteVatReleaseProperties(target: RecursiveDiff<VatReleaseDocumentModelPart, ModelBase>, source: RecursiveDiff<VatReleaseDocumentModelPart, ModelBase>, params: {companyParameters: CompanyParameters, vatRates: VatRateItem[] }): void {
    const exemptRate = params.vatRates.find(vatRate => vatRate.exempt);
    const isZwVatRateOnItems = exemptRate ? source.items.some(elem => elem.vatRateId === exemptRate.id) : false;
    const rewriteFromSource = isZwVatRateOnItems || (!source.issuedByVatPayer && !target.issuedByVatPayer);

    const properties = rewriteFromSource ? source : target.issuedByVatPayer ? null : params.companyParameters;

    if (properties) {
        target.vatReleaseReasonId = properties.vatReleaseReasonId;
        target.vatReleaseReasonCustomName = properties.vatReleaseReasonCustomName;
        target.vatReleaseReasonSource = properties.vatReleaseReasonSource;
    }
}

export function consolidatePaymentsIntoOne(target: RecursiveDiff<SaleAdvanceDocumentModel, ModelBase>, source: RecursiveDiff<SaleProFormaModel | SaleDocumentModel, ModelBase>, params: { precision: number }): void {
    if (source.payments.length === 0) return;

    const now = Date.now();
    const model = new DocumentPaymentListItemModel();
    if (source.payments.length === 1) {
        target.payments.push(model);
        rewritePayment(model, source.payments[0]);
    } else {
        const paymentsSummary = Calc.round(source.payments.reduce((sum, { value }) => sum + value, 0), params.precision, MidpointRounding.AwayFromZero);
        model.value = Math.abs(paymentsSummary);
        model.date = new Date(now);
        target.payments.push(model);
    }
}

export async function rewriteFromDocumentAsync(
    target: SaleDocumentModel | SaleProFormaModel | SaleAdvanceDocumentModel,
    source: SaleDocumentModel | SaleProFormaModel | SaleAdvanceDocumentModel,
    params: { companyParameters: CompanyParameters, communityTransactions: SaleCommunityTransactionItem[], defaultCalculationMethod: CalculationMethod, vatRates: VatRateItem[] },
    clientRepo: ClientRepositoryBase
): Promise<void> {
    target.issuedByVatPayer = params.companyParameters.vatPayer;
    if (saleDocumentModelWithProperty('cashBasis', target)) target.cashBasis = params.companyParameters.cashBasis;
    rewriteVatReleaseProperties(target, source, { companyParameters: params.companyParameters, vatRates: params.vatRates});

    target.communityTransactionId = source.communityTransactionId;
    if (target.kind === SaleDocumentKind.Receipt) {
        const acceptableTransactions = <SaleCommunityTransactionSymbol[]>['WSTO-OSS', 'SK', 'WSTO-SK'];
        const sourceTransaction = params.communityTransactions.find(elem => elem.id === source.communityTransactionId);
        const defaultTransaction = params.communityTransactions.find(elem => elem.default);
        const defaultTransactionId = defaultTransaction ? defaultTransaction.id : 0;
        target.communityTransactionId = (sourceTransaction && acceptableTransactions.includes(sourceTransaction.symbol))
            ? source.communityTransactionId
            : defaultTransactionId;
    }

    target.currencyId = source.currencyId;

    //#region order must be kept
    target.paymentMethodId = source.paymentMethodId;
    if (saleDocumentModelWithProperty('dueDate', target)) target.dueDate = source.dueDate;
    target.dueDateInDays = source.dueDateInDays;
    target.customDueDateDescription = source.customDueDateDescription;
    target.bankAccountNumberId = source.bankAccountNumberId;
    target.baseCurrencyBankAccountNumberId = source.baseCurrencyBankAccountNumberId;
    //#endregion
    target.calculationMethod = params.defaultCalculationMethod || (!target.issuedByVatPayer ? CalculationMethod.Gross : source.calculationMethod);
    target.note = source.note;
    target.jpkVatGroups = source.jpkVatGroups;

    target.wstoAdditionalInformation = source.wstoAdditionalInformation;
    target.wstoConsumerCountryId = source.wstoConsumerCountryId;
    target.wstoShipmentCountryId = source.wstoShipmentCountryId;

    await rewriteDocumentContractorWithFreshDataAsync(target, source, clientRepo);

    if (source.deliveryAddress) {
        const deliveryAddress = new AddressModel();
        rewriteAddress(deliveryAddress, source.deliveryAddress);
        target.deliveryAddress = deliveryAddress;
    }
}

export async function rewriteSalePatternFromSaleDocumentAsync(
    target: SalePatternModel,
    source: SaleDocumentModel,
    params: { numberings: NumberingItem[], companyParameters: CompanyParameters, communityTransactions: SaleCommunityTransactionItem[], defaultCalculationMethod: CalculationMethod | null, vatRates: VatRateItem[], loggedUser: UserCollectionItem },
    clientRepo: ClientRepositoryBase
): Promise<void> {
    target.issuedByVatPayer = params.companyParameters.vatPayer;
    rewriteVatReleaseProperties(target, source, {companyParameters: params.companyParameters, vatRates: params.vatRates});

    target.communityTransactionId = source.communityTransactionId;
    target.currencyId = source.currencyId;

    //#region order must be kept
    target.paymentMethodId = source.paymentMethodId;
    target.dueDateInDays = source.dueDateInDays;
    target.bankAccountNumberId = source.bankAccountNumberId;
    target.baseCurrencyBankAccountNumberId = source.baseCurrencyBankAccountNumberId;
    //#endregion
    target.calculationMethod = params.defaultCalculationMethod || (!target.issuedByVatPayer ? CalculationMethod.Gross : source.calculationMethod);
    target.note = source.note;
    target.splitPayment = source.splitPayment;
    target.active = true;
    target.jpkVatGroups = source.jpkVatGroups;
    target.invoiceToReceipt = !!source.invoiceToReceipt;

    target.wstoAdditionalInformation = source.wstoAdditionalInformation;
    target.wstoConsumerCountryId = source.wstoConsumerCountryId;
    target.wstoShipmentCountryId = source.wstoShipmentCountryId;

    target.ksefSendOption = SalePatternKsefSendOption.AccordingToConfiguration;

    await rewriteDocumentContractorWithFreshDataAsync(target, source, clientRepo);
    if (!target.client?.sourceClientId) {
        target.client = null;
        target.buyer = null;
    }

    if (source.deliveryAddress) {
        const deliveryAddress = new AddressModel();
        rewriteAddress(deliveryAddress, source.deliveryAddress);
        target.deliveryAddress = deliveryAddress;
    }

    target.transactionLocation = source.transactionLocation;
    target.recipient = source.recipient;

    const {firstName, lastName, id} = params.loggedUser;
    target.issuer = `${firstName} ${lastName}`;
    target.issuerUserId = id;

    target.numberingId = getCorrectNumberingIdFromSourceDocument(source.numberingId, params.numberings, target.kind);

    const wstoOssTx = params.communityTransactions.find(elem => elem.symbol === 'WSTO-OSS');
    const wasWstoOssTransaction = wstoOssTx 
        ? (source.communityTransactionId === wstoOssTx.id && !params.companyParameters.ossRegistered)
        : false;

    const defaultTx = params.communityTransactions.find(elem => elem.default);
    if (!target.issuedByVatPayer || wasWstoOssTransaction) {
        if (defaultTx) {
            target.communityTransactionId = defaultTx.id;
        }
    }

    const defaultVatRate = params.vatRates.find(elem => elem.default);
    const exemptVatRate = params.vatRates.find(elem => elem.exempt);

    source.items.forEach(item => {
        const model = new SaleDocumentItemModel();
        target.items.push(model);
        rewriteSaleDocumentItem(model, item);
        model.vatRateId = target.issuedByVatPayer
            ? (wasWstoOssTransaction ? (defaultVatRate?.id ?? item.vatRateId) : item.vatRateId)
            : (exemptVatRate?.id ?? item.vatRateId);
    });

    target.rateNP = source.rateNP;
    target.reverseCharge = source.reverseCharge;
}

export async function rewriteAdvanceInvoiceFromProFormaAsync(
    target: SaleAdvanceDocumentModel,
    source: SaleProFormaModel,
    params: { companyParameters: CompanyParameters, communityTransactions: SaleCommunityTransactionItem[], precision: number, defaultCalculationMethod: CalculationMethod, vatRates: VatRateItem[] },
    clientRepo: ClientRepositoryBase
): Promise<void> {
    await rewriteFromDocumentAsync(target, source, params, clientRepo);

    consolidatePaymentsIntoOne(target, source, { precision: params.precision });

    const wstoOssTxAdvance = params.communityTransactions.find(elem => elem.symbol === 'WSTO-OSS');
    const wasWstoOssTransaction = wstoOssTxAdvance
        ? (source.communityTransactionId === wstoOssTxAdvance.id && !params.companyParameters.ossRegistered)
        : false;

    const defaultTxAdvance = params.communityTransactions.find(elem => elem.default);
    if (wasWstoOssTransaction && defaultTxAdvance) {
        target.communityTransactionId = defaultTxAdvance.id;
    }

    const defaultVatRateAdvance = params.vatRates.find(elem => elem.default);

    source.items.map(item => {
        const model = new SaleAdvanceDocumentItemModel();
        target.items.push(model);
        rewriteSaleDocumentItem(model, item);
        model.vatRateId = wasWstoOssTransaction ? (defaultVatRateAdvance?.id ?? item.vatRateId) : item.vatRateId;
    });

    const now = Date.now();
    target.issueDate = new Date(now);
    if (target.issuedByVatPayer) {
        target.taxLiabilityDate = new Date(now);
    }
}

export async function rewriteSaleDocumentFromProFormaAsync(
    target: SaleDocumentModel,
    source: SaleProFormaModel,
    params: { companyParameters: CompanyParameters, communityTransactions: SaleCommunityTransactionItem[], defaultCalculationMethod: CalculationMethod, vatRates: VatRateItem[] },
    clientRepo: ClientRepositoryBase
): Promise<void> {
    await rewriteFromDocumentAsync(target, source, params, clientRepo);

    source.payments.map(item => {
        const model = new DocumentPaymentListItemModel();
        target.payments.push(model);
        rewritePayment(model, item);
    });

    const wstoTxSale = params.communityTransactions.find(elem => elem.symbol === 'WSTO-OSS');
    const wasWstoTransaction = wstoTxSale
        ? (source.communityTransactionId === wstoTxSale.id && !params.companyParameters.ossRegistered)
        : false;

    const defaultTxSale = params.communityTransactions.find(elem => elem.default);
    if (wasWstoTransaction && defaultTxSale) {
        target.communityTransactionId = defaultTxSale.id;
    }

    const defaultVatRateSale = params.vatRates.find(elem => elem.default);

    source.items.map(item => {
        const model = new SaleDocumentItemModel();
        target.items.push(model);
        rewriteSaleDocumentItem(model, item);
        model.vatRateId = wasWstoTransaction ? (defaultVatRateSale?.id ?? item.vatRateId) : item.vatRateId;
    });

    const now = Date.now();
    target.issueDate = new Date(now);
    target.saleDate = new Date(now);
    if (target.issuedByVatPayer) {
        target.taxLiabilityDate = new Date(now);
    }
}

export function rewritePlainObjectToDocumentModel(item: SaleGetDocumentDTO): SaleDocumentModel | SaleDocumentAdjustmentModel {
    if (isAdjustment(item.kind)) {
        const document = new SaleDocumentAdjustmentModel(pickBy<Partial<SaleDocumentAdjustmentModel>>({
            id: item.id,
            version: item.version,

            adjustmentReasonId: item.adjustmentReasonId,
            bankAccountNumberId: item.bankAccountId,
            baseCurrencyBankAccountNumberId: item.baseCurrencyBankAccountId,
            calculationMethod: item.calculationMethod,
            cashBasis: item.cashBasis,
            includesInventoryControl: item.includesInventoryControl,
            communityTransactionId: item.communityTransactionId,
            relatedInventoryDocuments: item.relatedInventoryDocuments,
            inventoryEffect: item.inventoryEffect,
            currencyId: item.currencyId,
            documentNumber: item.documentNumber,
            dueDate: item.dueDate,
            dueDateInDays: dateDiffInDays(item.dueDate, item.issueDate),
            favourite: item.favourite,
            grossValue: item.totalGrossValue,
            issueDate: item.issueDate,
            issuedByVatPayer: item.issuedByVatPayer,
            issuer: item.issuer,
            jpkVatGroups: [...item.jpkVatDocumentGroups, ...item.jpkVatProductGroups],
            kind: item.kind,
            marginSchemeId: item.marginSchemeId,
            netValue: item.totalNetValue,
            note: item.note,
            paymentMethodId: item.paymentMethodId,
            recipient: item.recipient,
            saleDate: item.deliveryDate,
            goodsIssueDate: item.goodsIssueDate || ((item.inventoryEffect && item.deliveryDate) || null),
            splitPayment: item.splitPayment,
            taxLiabilityDate: item.taxLiabilityDate,
            transactionLocation: item.transactionLocation,
            vatReleaseReasonId: item.vatReleaseReasonId,
            vatReleaseReasonCustomName: item.vatReleaseReasonCustomName,
            vatReleaseReasonSource: item.vatReleaseReasonSource,

            wstoAdditionalInformation: item.wstoAdditionalInformation,
            wstoConsumerCountryId: item.wstoConsumerCountryId,
            wstoShipmentCountryId: item.wstoShipmentCountryId,
            ksefSubmissionMode: item.ksefSubmissionMode,
            orderNumber: item.additionalData?.orderNumber,
            orderDate: item.additionalData?.orderDate,
            agreementNumber: item.additionalData?.agreementNumber,
            agreementDate: item.additionalData?.agreementDate,
            reverseCharge: item.reverseCharge,
            rateNP: item.rateNP,
        }, val => val !== void 0));

        document.client = deserializeDocumentClientModel(item.client);
        document.buyer = deserializeDocumentBuyerModel(item.buyer);

        document.deliveryAddress = item.deliveryAddress ? new AddressModel(pickBy<AddressModel>({
            name: item.deliveryAddress.name,
            zipCode: item.deliveryAddress.zipCode,
            line1: item.deliveryAddress.address,
            city: item.deliveryAddress.city,
            countryId: item.deliveryAddress.countryId
        }, val => val !== void 0)) : null as any;

        document.items = new ObservableCollection(item.items.map(documentItem => {
            return new SaleDocumentItemModel(pickBy<Partial<SaleDocumentItemModel>>({
                cnCode: documentItem.cnCode,
                symbol: documentItem.symbol,
                barCode: documentItem.barCode,
                cost: documentItem.cost,
                ordinalNumber: documentItem.no,
                discountKind: documentItem.discountKind,
                discountValue: documentItem.discountValue,
                jpkVatGroups: documentItem.jpkVatGroups,
                includesInventoryControl: documentItem.includesInventoryControl,
                jpkVatProcedure: documentItem.jpkVatProcedure,
                note: documentItem.note,
                price_grossAfterDiscount: documentItem.unitGrossPriceAfterDiscount,
                price_grossBeforeDiscount: documentItem.unitGrossPrice,
                price_netAfterDiscount: documentItem.unitNetPriceAfterDiscount,
                price_netBeforeDiscount: documentItem.unitNetPrice,
                productId: documentItem.productId,
                productName: documentItem.productName,
                pkwiu: documentItem.pkwiu,
                quantity: documentItem.quantity,
                unitCost: documentItem.unitCost,
                unitId: documentItem.unitId,
                value_grossAfterDiscount: documentItem.grossValueAfterDiscount,
                value_grossBeforeDiscount: documentItem.grossValue,
                value_netAfterDiscount: documentItem.netValueAfterDiscount,
                value_netBeforeDiscount: documentItem.netValue,
                value_vatAfterDiscount: documentItem.vatValueAfterDiscount,
                value_vatBeforeDiscount: documentItem.vatValue,
                vatRateId: documentItem.vatRateId
            }, val => val !== void 0));
        }));

        if (item.exchangeRate) {
            document.exchangeRate = new ExchangeRateModel(pickBy<ExchangeRateModel>({
                date: item.exchangeRate.date,
                rate: item.exchangeRate.rate,
                units: item.exchangeRate.units
            }, val => val !== void 0));
        }

        document.vatTableItems = new ObservableCollection(item.vatTable.items.map(item => {
            return new VatTableItemModel(pickBy<Partial<VatTableItemModel>>({
                grossValue: item.currencyGross ?? item.gross,
                netValue: item.currencyNet ?? item.net,
                vatRateId: item.vatRateId,
                vatValue: item.currencyVat ?? item.vat,
            }, val => val !== void 0));
        }));

        document.vatTableItemsInBaseCurrency = new ObservableCollection(item.vatTable.items.map(item => {
            return item.currencyGross != null ? new VatTableItemModel(pickBy<Partial<VatTableItemModel>>({
                grossValue: item.gross,
                netValue: item.net,
                vatRateId: item.vatRateId,
                vatValue: item.vat
            }, val => val !== void 0)) : null;
        }).filter(item => item !== null));

        document.payments = new ObservableCollection(item.payments.map(item => {
            return new DocumentPaymentListItemModel(pickBy<Partial<DocumentPaymentListItemModel>>({
                id: item.id,
                date: item.date,
                value: item.amount
            }, val => val !== void 0));
        }));

        return document;
    } else {
        const document = new SaleDocumentModel(pickBy<Partial<SaleDocumentModel>>({
            id: item.id,
            version: item.version,

            ddkState: item.ddkState,
            eppState: item.eppState,
            fiscalizationState: item.fiscalizationState,
            jpk_VATState: item.jpk_vatState,
            jpkVatGroups: [...item.jpkVatDocumentGroups, ...item.jpkVatProductGroups],
            orderState: item.orderState,
            includesInventoryControl: item.includesInventoryControl,
            inventoryEffect: item.inventoryEffect,

            bankAccountNumberId: item.bankAccountId,
            baseCurrencyBankAccountNumberId: item.baseCurrencyBankAccountId,
            calculationMethod: item.calculationMethod,
            cashBasis: item.cashBasis,
            communityTransactionId: item.communityTransactionId,
            currencyId: item.currencyId,
            relatedInventoryDocuments: item.relatedInventoryDocuments,
            documentNumber: item.documentNumber,
            dueDate: item.dueDate,
            dueDateInDays: dateDiffInDays(item.dueDate, item.issueDate),
            favourite: item.favourite,
            grossValue: item.totalGrossValue,

            goodsIssueDate: item.goodsIssueDate || ((item.inventoryEffect && item.deliveryDate) || null),
            issueDate: item.issueDate,
            issuedByVatPayer: item.issuedByVatPayer,
            issuer: item.issuer,
            kind: item.kind,
            marginSchemeId: item.marginSchemeId,
            netValue: item.totalNetValue,
            note: item.note,
            paymentMethodId: item.paymentMethodId,
            recipient: item.recipient,
            saleDate: item.deliveryDate,
            splitPayment: item.splitPayment,
            taxLiabilityDate: item.taxLiabilityDate,
            transactionLocation: item.transactionLocation,
            vatReleaseReasonId: item.vatReleaseReasonId,
            vatReleaseReasonCustomName: item.vatReleaseReasonCustomName,
            vatReleaseReasonSource: item.vatReleaseReasonSource,

            wstoAdditionalInformation: item.wstoAdditionalInformation,
            wstoConsumerCountryId: item.wstoConsumerCountryId,
            wstoShipmentCountryId: item.wstoShipmentCountryId,
            ksefSubmissionMode: item.ksefSubmissionMode,
            orderNumber: item.additionalData?.orderNumber,
            orderDate: item.additionalData?.orderDate,
            agreementNumber: item.additionalData?.agreementNumber,
            agreementDate: item.additionalData?.agreementDate,
            reverseCharge: item.reverseCharge,
            rateNP: item.rateNP,
        }, val => val !== void 0));

        document.client = deserializeDocumentClientModel(item.client);
        document.buyer = deserializeDocumentBuyerModel(item.buyer);

        document.deliveryAddress = item.deliveryAddress ? new AddressModel(pickBy<AddressModel>({
            name: item.deliveryAddress.name,
            zipCode: item.deliveryAddress.zipCode,
            line1: item.deliveryAddress.address,
            city: item.deliveryAddress.city,
            countryId: item.deliveryAddress.countryId
        }, val => val !== void 0)) : null as any;

        document.items = new ObservableCollection(item.items.map(documentItem => {
            return new SaleDocumentItemModel(pickBy<Partial<SaleDocumentItemModel>>({
                cnCode: documentItem.cnCode,
                symbol: documentItem.symbol,
                barCode: documentItem.barCode,
                cost: documentItem.cost,
                ordinalNumber: documentItem.no,
                discountKind: documentItem.discountKind,
                discountValue: documentItem.discountValue,
                jpkVatGroups: documentItem.jpkVatGroups,
                jpkVatProcedure: documentItem.jpkVatProcedure,
                includesInventoryControl: documentItem.includesInventoryControl,
                note: documentItem.note,
                price_grossAfterDiscount: documentItem.unitGrossPriceAfterDiscount,
                price_grossBeforeDiscount: documentItem.unitGrossPrice,
                price_netAfterDiscount: documentItem.unitNetPriceAfterDiscount,
                price_netBeforeDiscount: documentItem.unitNetPrice,
                productId: documentItem.productId,
                productName: documentItem.productName,
                pkwiu: documentItem.pkwiu,
                quantity: documentItem.quantity,
                unitCost: documentItem.unitCost,
                unitId: documentItem.unitId,
                value_grossAfterDiscount: documentItem.grossValueAfterDiscount,
                value_grossBeforeDiscount: documentItem.grossValue,
                value_netAfterDiscount: documentItem.netValueAfterDiscount,
                value_netBeforeDiscount: documentItem.netValue,
                value_vatAfterDiscount: documentItem.vatValueAfterDiscount,
                value_vatBeforeDiscount: documentItem.vatValue,
                vatRateId: documentItem.vatRateId
            }, val => val !== void 0));
        }));

        if (item.exchangeRate) {
            document.exchangeRate = new ExchangeRateModel(pickBy<ExchangeRateModel>({
                date: item.exchangeRate.date,
                rate: item.exchangeRate.rate,
                units: item.exchangeRate.units
            }, val => val !== void 0));
        }

        document.payments = new ObservableCollection(item.payments.map(item => {
            return new DocumentPaymentListItemModel(pickBy<Partial<DocumentPaymentListItemModel>>({
                id: item.id,
                date: item.date,
                value: item.amount
            }, val => val !== void 0));
        }));

        document.vatTableItems = new ObservableCollection(item.vatTable.items.map(item => {
            return new VatTableItemModel(pickBy<Partial<VatTableItemModel>>({
                grossValue: item.currencyGross ?? item.gross,
                netValue: item.currencyNet ?? item.net,
                vatRateId: item.vatRateId,
                vatValue: item.currencyVat ?? item.vat,
            }, val => val !== void 0));
        }));

        document.vatTableItemsInBaseCurrency = item.exchangeRate ? new ObservableCollection(item.vatTable.items.map(item => {
            return new VatTableItemModel(pickBy<Partial<VatTableItemModel>>({
                grossValue: item.gross,
                netValue: item.net,
                vatRateId: item.vatRateId,
                vatValue: item.vat
            }, val => val !== void 0));
        })) : new ObservableCollection();

        return document;
    }
}

export function rewriteAdvancePlainObjectToDocumentModel(item: SaleGetDocumentDTO): SaleAdvanceDocumentModel {
    const document = new SaleAdvanceDocumentModel(pickBy<Partial<SaleAdvanceDocumentModel>>({
        id: item.id,
        version: item.version,

        ddkState: item.ddkState,
        eppState: item.eppState,
        fiscalizationState: item.fiscalizationState,
        jpk_VATState: item.jpk_vatState,
        jpkVatGroups: [...item.jpkVatDocumentGroups, ...item.jpkVatProductGroups],
        invoiceToReceipt: item.invoiceToReceipt,
        orderState: item.orderState,
        includesInventoryControl: item.includesInventoryControl,

        bankAccountNumberId: item.bankAccountId,
        baseCurrencyBankAccountNumberId: item.baseCurrencyBankAccountId,
        calculationMethod: item.calculationMethod,
        cashBasis: item.cashBasis,
        communityTransactionId: item.communityTransactionId,
        currencyId: item.currencyId,
        documentNumber: item.documentNumber,
        dueDate: item.dueDate,
        dueDateInDays: dateDiffInDays(item.dueDate, item.issueDate),
        favourite: item.favourite,
        grossValue: item.totalGrossValue,

        issueDate: item.issueDate,
        issuedByVatPayer: item.issuedByVatPayer,
        issuer: item.issuer,
        kind: item.kind,
        marginSchemeId: item.marginSchemeId,
        netValue: item.totalNetValue,
        note: item.note,
        numberingId: item.numberingId,
        paymentMethodId: item.paymentMethodId,
        recipient: item.recipient,
        splitPayment: item.splitPayment,
        taxLiabilityDate: item.taxLiabilityDate,
        transactionLocation: item.transactionLocation,
        vatReleaseReasonId: item.vatReleaseReasonId,
        vatReleaseReasonCustomName: item.vatReleaseReasonCustomName,
        vatReleaseReasonSource: item.vatReleaseReasonSource,

        wstoAdditionalInformation: item.wstoAdditionalInformation,
        wstoConsumerCountryId: item.wstoConsumerCountryId,
        wstoShipmentCountryId: item.wstoShipmentCountryId,
        ksefSubmissionMode: item.ksefSubmissionMode,
        orderNumber: item.additionalData?.orderNumber,
        orderDate: item.additionalData?.orderDate,
        agreementNumber: item.additionalData?.agreementNumber,
        agreementDate: item.additionalData?.agreementDate,
        reverseCharge: item.reverseCharge,
        rateNP: item.rateNP,
    }, val => val !== void 0));

    document.client = deserializeDocumentClientModel(item.client);
    document.buyer = deserializeDocumentBuyerModel(item.buyer);

    document.deliveryAddress = item.deliveryAddress ? new AddressModel(pickBy<AddressModel>({
        name: item.deliveryAddress.name,
        zipCode: item.deliveryAddress.zipCode,
        line1: item.deliveryAddress.address,
        city: item.deliveryAddress.city,
        countryId: item.deliveryAddress.countryId
    }, val => val !== void 0)) : null as any;

    document.items = new ObservableCollection(item.items.map(documentItem => {
        return new SaleAdvanceDocumentItemModel(pickBy<Partial<SaleAdvanceDocumentItemModel>>({
            cnCode: documentItem.cnCode,
            symbol: documentItem.symbol,
            barCode: documentItem.barCode,
            pkwiu: documentItem.pkwiu,
            cost: documentItem.cost,
            ordinalNumber: documentItem.no,
            discountKind: documentItem.discountKind,
            discountValue: documentItem.discountValue,
            jpkVatGroups: documentItem.jpkVatGroups,
            jpkVatProcedure: documentItem.jpkVatProcedure,
            note: documentItem.note,
            includesInventoryControl: documentItem.includesInventoryControl,
            price_grossAfterDiscount: documentItem.unitGrossPriceAfterDiscount,
            price_grossBeforeDiscount: documentItem.unitGrossPrice,
            price_netAfterDiscount: documentItem.unitNetPriceAfterDiscount,
            price_netBeforeDiscount: documentItem.unitNetPrice,
            productId: documentItem.productId,
            productName: documentItem.productName,
            quantity: documentItem.quantity,
            unitCost: documentItem.unitCost,
            unitId: documentItem.unitId,
            value_grossAfterDiscount: documentItem.grossValueAfterDiscount,
            value_grossBeforeDiscount: documentItem.grossValue,
            value_netAfterDiscount: documentItem.netValueAfterDiscount,
            value_netBeforeDiscount: documentItem.netValue,
            value_vatAfterDiscount: documentItem.vatValueAfterDiscount,
            value_vatBeforeDiscount: documentItem.vatValue,
            vatRateId: documentItem.vatRateId
        }, val => val !== void 0));
    }));

    if (item.exchangeRate) {
        document.exchangeRate = new ExchangeRateModel(pickBy<ExchangeRateModel>({
            date: item.exchangeRate.date,
            rate: item.exchangeRate.rate,
            units: item.exchangeRate.units
        }, val => val !== void 0));
    }

    document.payments = new ObservableCollection(item.payments.map(item => {
        return new DocumentPaymentListItemModel(pickBy<Partial<DocumentPaymentListItemModel>>({
            id: item.id,
            date: item.date,
            value: item.amount
        }, val => val !== void 0));
    }));

    document.vatTableItems = new ObservableCollection(item.vatTable.items.map(item => {
        return new VatTableItemModel(pickBy<Partial<VatTableItemModel>>({
            grossValue: item.currencyGross ?? item.gross,
            netValue: item.currencyNet ?? item.net,
            vatRateId: item.vatRateId,
            vatValue: item.currencyVat ?? item.vat,
        }, val => val !== void 0));
    }));

    document.vatTableItemsInBaseCurrency = item.exchangeRate ? new ObservableCollection(item.vatTable.items.map(item => {
        return new VatTableItemModel(pickBy<Partial<VatTableItemModel>>({
            grossValue: item.gross,
            netValue: item.net,
            vatRateId: item.vatRateId,
            vatValue: item.vat
        }, val => val !== void 0));
    })) : new ObservableCollection();

    return document;
}

export function rewritePlainObjectToAdvanceDocumentModel(item: SaleGetDocumentDTO): SaleAdvanceDocumentModel {

    const document = new SaleAdvanceDocumentModel(pickBy<Partial<SaleAdvanceDocumentModel>>({
        id: item.id,
        version: item.version,

        ddkState: item.ddkState,
        eppState: item.eppState,
        fiscalizationState: item.fiscalizationState,
        jpk_VATState: item.jpk_vatState,
        jpkVatGroups: [...item.jpkVatProductGroups, ...item.jpkVatDocumentGroups],
        orderState: item.orderState,
        includesInventoryControl: item.includesInventoryControl,

        bankAccountNumberId: item.bankAccountId,
        baseCurrencyBankAccountNumberId: item.baseCurrencyBankAccountId,
        calculationMethod: item.calculationMethod,
        cashBasis: item.cashBasis,
        communityTransactionId: item.communityTransactionId,
        currencyId: item.currencyId,
        documentNumber: item.documentNumber,
        dueDate: item.dueDate,
        dueDateInDays: dateDiffInDays(item.dueDate, item.issueDate),
        favourite: item.favourite,
        grossValue: item.totalGrossValue,

        splitPayment: item.splitPayment,
        saleDate: item.deliveryDate,
        issueDate: item.issueDate,
        issuedByVatPayer: item.issuedByVatPayer,
        issuer: item.issuer,
        kind: item.kind,
        marginSchemeId: item.marginSchemeId,
        netValue: item.totalNetValue,
        note: item.note,
        paymentMethodId: item.paymentMethodId,
        recipient: item.recipient,
        taxLiabilityDate: item.taxLiabilityDate,
        transactionLocation: item.transactionLocation,
        vatReleaseReasonId: item.vatReleaseReasonId,
        vatReleaseReasonCustomName: item.vatReleaseReasonCustomName,
        vatReleaseReasonSource: item.vatReleaseReasonSource,

        wstoAdditionalInformation: item.wstoAdditionalInformation,
        wstoConsumerCountryId: item.wstoConsumerCountryId,
        wstoShipmentCountryId: item.wstoShipmentCountryId,
        ksefSubmissionMode: item.ksefSubmissionMode,
        orderNumber: item.additionalData?.orderNumber,
        orderDate: item.additionalData?.orderDate,
        agreementNumber: item.additionalData?.agreementNumber,
        agreementDate: item.additionalData?.agreementDate,
        reverseCharge: item.reverseCharge,
        rateNP: item.rateNP,
    }, val => val !== void 0));

    document.client = deserializeDocumentClientModel(item.client);
    document.buyer = deserializeDocumentBuyerModel(item.buyer);

    document.deliveryAddress = item.deliveryAddress ? new AddressModel(pickBy<AddressModel>({
        name: item.deliveryAddress.name,
        zipCode: item.deliveryAddress.zipCode,
        line1: item.deliveryAddress.address,
        city: item.deliveryAddress.city,
        countryId: item.deliveryAddress.countryId
    }, val => val !== void 0)) : null as any;

    document.items = new ObservableCollection(item.items.map(documentItem => {
        return new SaleAdvanceDocumentItemModel(pickBy<Partial<SaleAdvanceDocumentItemModel>>({
            cnCode: documentItem.cnCode,
            symbol: documentItem.symbol,
            barCode: documentItem.barCode,
            pkwiu: documentItem.pkwiu,
            cost: documentItem.cost,
            ordinalNumber: documentItem.no,
            discountKind: documentItem.discountKind,
            discountValue: documentItem.discountValue,
            jpkVatGroups: documentItem.jpkVatGroups,
            jpkVatProcedure: documentItem.jpkVatProcedure,
            note: documentItem.note,
            price_grossAfterDiscount: documentItem.unitGrossPriceAfterDiscount,
            price_grossBeforeDiscount: documentItem.unitGrossPrice,
            price_netAfterDiscount: documentItem.unitNetPriceAfterDiscount,
            price_netBeforeDiscount: documentItem.unitNetPrice,
            productId: documentItem.productId,
            productName: documentItem.productName,
            quantity: documentItem.quantity,
            unitCost: documentItem.unitCost,
            unitId: documentItem.unitId,
            value_grossAfterDiscount: documentItem.grossValueAfterDiscount,
            includesInventoryControl: documentItem.includesInventoryControl,
            value_grossBeforeDiscount: documentItem.grossValue,
            value_netAfterDiscount: documentItem.netValueAfterDiscount,
            value_netBeforeDiscount: documentItem.netValue,
            value_vatAfterDiscount: documentItem.vatValueAfterDiscount,
            value_vatBeforeDiscount: documentItem.vatValue,
            vatRateId: documentItem.vatRateId
        }, val => val !== void 0));
    }));

    if (item.exchangeRate) {
        document.exchangeRate = new ExchangeRateModel(pickBy<ExchangeRateModel>({
            date: item.exchangeRate.date,
            rate: item.exchangeRate.rate,
            units: item.exchangeRate.units
        }, val => val !== void 0));
    }

    document.payments = new ObservableCollection(item.payments.map(item => {
        return new DocumentPaymentListItemModel(pickBy<Partial<DocumentPaymentListItemModel>>({
            id: item.id,
            date: item.date,
            value: item.amount
        }, val => val !== void 0));
    }));

    document.vatTableItems = new ObservableCollection(item.vatTable.items.map(item => {
        return new VatTableItemModel(pickBy<Partial<VatTableItemModel>>({
            grossValue: item.currencyGross ?? item.gross,
            netValue: item.currencyNet ?? item.net,
            vatRateId: item.vatRateId,
            vatValue: item.currencyVat ?? item.vat,
        }, val => val !== void 0));
    }));

    document.vatTableItemsInBaseCurrency = item.exchangeRate ? new ObservableCollection(item.vatTable.items.map(item => {
        return new VatTableItemModel(pickBy<Partial<VatTableItemModel>>({
            grossValue: item.gross,
            netValue: item.net,
            vatRateId: item.vatRateId,
            vatValue: item.vat
        }, val => val !== void 0));
    })) : new ObservableCollection();

    return document;
}

// //#endregion

type WithAllSaleDocumentPropertiesModel = SaleDocumentModel | SaleProFormaModel | SaleAdvanceDocumentModel;
function saleDocumentModelWithProperty(
    property: keyof WithAllSaleDocumentPropertiesModel,
    model: RecursiveDiff<WithAllSaleDocumentPropertiesModel | SalePatternModel, ModelBase>
): model is RecursiveDiff<WithAllSaleDocumentPropertiesModel, ModelBase> {
    return model.hasOwnProperty(property);
}
